import { isCalendarDate } from '../accounting';
import { addCalendarDays, calendarDaysBetween } from './dates';

/**
 * Biorhythm (SC-29): four sine cycles that start on the day of birth. It is
 * offered for reflection only, and every screen that shows it carries
 * `COACHING_BIORHYTHM_DISCLAIMER`. The level shown is an index of where a cycle
 * sits between its low and its high, never a probability.
 *
 * The blueprint's SC-29 names three cycles. The fourth, spiritual, comes from
 * the prototype's own biorhythm tab and is carried here as its own plan
 * feature (`biorhythm_spiritual`), so a tenant decides whether it is shown.
 *
 * Pure and computed on the member's own device from their birth date and
 * their member-local day, so the screen costs no request and no credits.
 */

export const BIORHYTHM_CYCLES = ['physical', 'emotional', 'intellectual', 'spiritual'] as const;
export type BiorhythmCycle = (typeof BIORHYTHM_CYCLES)[number];

/** The three the blueprint names. The lucky tables key off these alone. */
export const BIORHYTHM_CLASSIC_CYCLES = ['physical', 'emotional', 'intellectual'] as const;

/** Cycle lengths in days. */
export const BIORHYTHM_PERIODS: Record<BiorhythmCycle, number> = {
  physical: 23,
  emotional: 28,
  intellectual: 33,
  spiritual: 53,
};

export const BIORHYTHM_CYCLE_LABELS: Record<BiorhythmCycle, string> = {
  physical: 'Physical',
  emotional: 'Emotional',
  intellectual: 'Intellectual',
  spiritual: 'Spiritual',
};

export const BIORHYTHM_PHASES = ['peak', 'rising', 'critical', 'declining', 'low'] as const;
export type BiorhythmPhase = (typeof BIORHYTHM_PHASES)[number];

export const BIORHYTHM_PHASE_LABELS: Record<BiorhythmPhase, string> = {
  peak: 'High',
  rising: 'Rising',
  critical: 'Turning point',
  declining: 'Easing',
  low: 'Recharge',
};

/** Shown wherever a biorhythm is displayed (blueprint SC-29). */
export const COACHING_BIORHYTHM_DISCLAIMER =
  'Biorhythm information is provided for personal reflection and is not scientifically established as a predictor of performance or outcomes.';

/** Below this distance from zero a cycle is crossing its midline. */
export const BIORHYTHM_CRITICAL_BAND = 0.12;
/** At or beyond this distance from zero a cycle is at its high or its low. */
export const BIORHYTHM_EXTREME_BAND = 0.65;
/** Days either side of today in the chart window. */
export const BIORHYTHM_WINDOW_DAYS = 7;

const NOTES: Record<BiorhythmCycle, Record<BiorhythmPhase, string>> = {
  physical: {
    peak: 'Your physical cycle is high. A good day for demanding tasks, training or long working blocks.',
    rising:
      'Your physical energy is building. Plan active work for the hours you usually feel strongest.',
    critical:
      'Your physical cycle is at a turning point. Pace yourself and avoid overcommitting your body today.',
    declining:
      'Your physical cycle is easing. Keep your effort steady and leave room for recovery.',
    low: 'Your physical cycle is in a recharge phase. Favor lighter tasks, rest and gentle movement.',
  },
  emotional: {
    peak: 'Your emotional cycle is high. A good day for conversations, collaboration and creative work.',
    rising: 'Your emotional energy is building. Reach out to the people who matter to your goal.',
    critical:
      'Your emotional cycle is at a turning point. Give your reactions a moment before you act on them.',
    declining: 'Your emotional cycle is easing. Protect your calm and keep commitments simple.',
    low: 'Your emotional cycle is in a recharge phase. Be patient with yourself and choose supportive company.',
  },
  intellectual: {
    peak: 'Your intellectual cycle is elevated today. Consider prioritizing strategy, planning, learning or complex decisions.',
    rising: 'Your mental focus is building. Line up the problems that need clear thinking.',
    critical:
      'Your intellectual cycle is at a turning point. Double-check the details before you commit.',
    declining:
      'Your intellectual cycle is easing. Finish work in progress before starting complex new analysis.',
    low: 'Your intellectual cycle is in a recharge phase. Choose routine tasks and revisit big decisions tomorrow.',
  },
  spiritual: {
    peak: 'Your spiritual cycle is high. You may find meditation, mantra and quiet inner work land more easily today.',
    rising:
      'Your spiritual cycle is building. Give your practice a little more room than usual this week.',
    critical:
      'Your spiritual cycle is at a turning point. Consider sitting with the open questions rather than reaching for answers.',
    declining:
      'Your spiritual cycle is easing. Keep your practice short and familiar rather than starting something new.',
    low: 'Your spiritual cycle is in a recharge phase. Favor silence and rest, and let insight come in its own time.',
  },
};

/** Where a cycle stands `daysSinceBirth` days in: -1 (low) to 1 (high). */
export function biorhythmValue(daysSinceBirth: number, periodDays: number): number {
  return Math.sin((2 * Math.PI * daysSinceBirth) / periodDays);
}

/**
 * The phase of a cycle today. Near the midline it is a turning point; far from
 * it, a high or a recharge; in between, whether tomorrow is higher decides
 * whether it is rising or easing.
 */
export function biorhythmPhase(value: number, tomorrow: number): BiorhythmPhase {
  if (Math.abs(value) < BIORHYTHM_CRITICAL_BAND) return 'critical';
  if (value >= BIORHYTHM_EXTREME_BAND) return 'peak';
  if (value <= -BIORHYTHM_EXTREME_BAND) return 'low';
  return tomorrow >= value ? 'rising' : 'declining';
}

/** A 0-100 display index of a cycle's position between its low and its high. */
export function biorhythmLevel(value: number): number {
  return Math.round(((value + 1) / 2) * 100);
}

export type BiorhythmReading = {
  cycle: BiorhythmCycle;
  value: number;
  level: number;
  phase: BiorhythmPhase;
  note: string;
};

export type BiorhythmDay = { day: string; values: Record<BiorhythmCycle, number> };

export type Biorhythm = {
  day: string;
  daysSinceBirth: number;
  /** The cycles this member is shown, in display order. */
  cycles: readonly BiorhythmCycle[];
  /** Every cycle is computed; `cycles` says which of them to show. */
  today: Record<BiorhythmCycle, BiorhythmReading>;
  /** The shown cycle standing highest today; its note leads the screen. */
  strongest: BiorhythmCycle;
  /** `2 * BIORHYTHM_WINDOW_DAYS + 1` days centred on `day`. */
  window: BiorhythmDay[];
};

function valuesAt(daysSinceBirth: number): Record<BiorhythmCycle, number> {
  return {
    physical: biorhythmValue(daysSinceBirth, BIORHYTHM_PERIODS.physical),
    emotional: biorhythmValue(daysSinceBirth, BIORHYTHM_PERIODS.emotional),
    intellectual: biorhythmValue(daysSinceBirth, BIORHYTHM_PERIODS.intellectual),
    spiritual: biorhythmValue(daysSinceBirth, BIORHYTHM_PERIODS.spiritual),
  };
}

/**
 * The member's biorhythm on `day` (both `YYYY-MM-DD`). Null when there is no
 * birth date, when either date is not a real calendar date, or when `day` is
 * before the birth date.
 *
 * `cycles` is the set the member's plan shows. Every cycle is still computed;
 * only `cycles` and `strongest` narrow, so the caller cannot lead a screen
 * with a note about a cycle it is not showing.
 */
export function computeBiorhythm(
  birthDate: string | null | undefined,
  day: string,
  cycles: readonly BiorhythmCycle[] = BIORHYTHM_CYCLES,
): Biorhythm | null {
  if (!birthDate || !isCalendarDate(birthDate) || !isCalendarDate(day)) return null;
  const daysSinceBirth = calendarDaysBetween(birthDate, day);
  if (daysSinceBirth < 0) return null;

  const shown = cycles.length > 0 ? cycles : BIORHYTHM_CYCLES;
  const now = valuesAt(daysSinceBirth);
  const next = valuesAt(daysSinceBirth + 1);
  const reading = (cycle: BiorhythmCycle): BiorhythmReading => {
    const phase = biorhythmPhase(now[cycle], next[cycle]);
    return {
      cycle,
      value: now[cycle],
      level: biorhythmLevel(now[cycle]),
      phase,
      note: NOTES[cycle][phase],
    };
  };
  const today = {
    physical: reading('physical'),
    emotional: reading('emotional'),
    intellectual: reading('intellectual'),
    spiritual: reading('spiritual'),
  };
  const strongest = shown.reduce((best, cycle) =>
    today[cycle].value > today[best].value ? cycle : best,
  );
  const window = Array.from({ length: BIORHYTHM_WINDOW_DAYS * 2 + 1 }, (_, i) => {
    const offset = i - BIORHYTHM_WINDOW_DAYS;
    return { day: addCalendarDays(day, offset), values: valuesAt(daysSinceBirth + offset) };
  });
  return { day, daysSinceBirth, cycles: shown, today, strongest, window };
}
